import { useMemo } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEdges, useNodes } from '@/components/Recipe/FlowContext';
import { getPrecedingIterators, getPrecedingNodesUntilIterator, type PrecedingIteratorData } from '@/utils/flow';
import { FF_RUN_ITERATORS_WITH_NODES_TRAVERSAL } from '@/consts/featureFlags';
import type { NodeId } from 'web';

export const usePrecedingIterators = (nodeIds: NodeId[] | NodeId): PrecedingIteratorData[] | null => {
  const nodes = useNodes();
  const edges = useEdges();
  const useNodesTraversal = useFeatureFlagEnabled(FF_RUN_ITERATORS_WITH_NODES_TRAVERSAL);
  const precedingIterators = useMemo(() => {
    const nodeIdArray = Array.isArray(nodeIds) ? nodeIds : [nodeIds];
    // Create a set of node IDs to allow traversal through model nodes in the batch
    const modelNodeIds = new Set<NodeId>(nodeIdArray);

    const iterators = nodeIdArray
      .map((nodeId) => {
        if (useNodesTraversal) {
          return getPrecedingNodesUntilIterator(nodeId, nodes, edges, modelNodeIds)?.iterators ?? [];
        }
        return getPrecedingIterators(nodeId, nodes, edges) ?? [];
      })
      .flat()
      .filter((iterator) => iterator !== null);

    if (iterators.length === 0) return null;

    // Deduplicate iterators based on iterator nodeId
    const uniqueIterators = Array.from(
      new Map(iterators.map((iterator) => [`${iterator.iteratorNode.id}`, iterator])).values(),
    );

    return uniqueIterators;
  }, [edges, nodeIds, nodes, useNodesTraversal]);

  return precedingIterators;
};

export type { PrecedingIteratorData };
